import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { ExceedanceCurve } from "@/components/ExceedanceCurve";
import { toast } from "sonner";
import {
  Play, Loader2, Save, Trash2, ArrowRight, Gauge, Banknote, AlertTriangle,
  GitCompareArrows, FlaskConical, Layers, ShieldCheck, Target, TrendingDown,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from "recharts";

const CURRENCIES = ["GBP", "USD", "EUR"];
const fmt = (v, cur = "GBP") =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: cur, notation: "compact", maximumFractionDigits: 1 }).format(v || 0);
const posTone = (d) => (d > 0 ? "text-red-600" : d < 0 ? "text-emerald-600" : "text-ek-ink");

const TYPES = [
  { key: "supplier", label: "Tier-1 supplier" },
  { key: "country", label: "Country" },
  { key: "theme", label: "Risk theme" },
  { key: "geo", label: "Geopolitical" },
];
const GEO_KINDS = [
  { key: "dyad", label: "Bilateral tension" },
  { key: "chokepoint", label: "Trade chokepoint" },
  { key: "material", label: "Critical material" },
  { key: "visa", label: "Labour mobility" },
];
const COMPARE_COLORS = ["#7737F6", "#0ea5e9", "#f59e0b", "#ef4444"];

export default function ScenarioStudio() {
  const [options, setOptions] = useState(null);
  const [type, setType] = useState("geo");
  const [target, setTarget] = useState("");
  const [geoKind, setGeoKind] = useState("chokepoint");
  const [severity, setSeverity] = useState(0.7);
  const [currency, setCurrency] = useState("GBP");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("forecast");

  // Mitigation what-if
  const [mitControl, setMitControl] = useState(0.4);
  const [mitInventory, setMitInventory] = useState(0.3);
  const [mitAlt, setMitAlt] = useState(true);
  const [mitResult, setMitResult] = useState(null);
  const [mitLoading, setMitLoading] = useState(false);

  // Reverse stress test
  const [revMetric, setRevMetric] = useState("posture");
  const [revThreshold, setRevThreshold] = useState(90);
  const [revResult, setRevResult] = useState(null);
  const [revLoading, setRevLoading] = useState(false);

  const [library, setLibrary] = useState([]);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState([]);
  const [compare, setCompare] = useState(null);
  const [comparing, setComparing] = useState(false);

  const loadLibrary = () =>
    api.get("/scenario/library").then(({ data }) => setLibrary(data.scenarios)).catch(() => {});

  useEffect(() => {
    api.get("/scenario/options").then(({ data }) => setOptions(data)).catch(() => {});
    loadLibrary();
  }, []);

  const targetList = useMemo(() => (
    type === "supplier" ? (options?.suppliers || []).map((s) => ({ id: s.supplier_id, label: `${s.legal_name} (${s.country})` }))
      : type === "country" ? (options?.countries || []).map((c) => ({ id: c, label: c }))
        : type === "geo" ? (options?.geo?.[geoKind] || []).map((g) => ({ id: g.id, label: g.label }))
          : (options?.themes || []).map((t) => ({ id: t.theme_id, label: t.name }))
  ), [options, type, geoKind]);

  const effectiveTarget = targetList.some((t) => t.id === target) ? target : targetList[0]?.id || "";

  const buildShock = () =>
    type === "geo"
      ? { type: "geo", geo: { shock_type: geoKind, target: effectiveTarget }, severity }
      : { type, target_ids: [effectiveTarget], severity };

  const run = async () => {
    if (!effectiveTarget) return;
    setLoading(true);
    try {
      const { data } = await api.post("/scenario/simulate", { shock: buildShock(), currency });
      setResult(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Simulation failed.");
    } finally {
      setLoading(false);
    }
  };

  const runMitigate = async () => {
    if (!effectiveTarget) return;
    setMitLoading(true);
    try {
      const mitigations = [];
      if (mitControl > 0) mitigations.push({ type: "control_effectiveness", value: mitControl });
      if (mitInventory > 0) mitigations.push({ type: "inventory_buffer", value: mitInventory });
      if (mitAlt) mitigations.push({ type: "alternate_supplier" });
      if (!mitigations.length) return toast.error("Select at least one mitigation.");
      const { data } = await api.post("/scenario/mitigate", { shock: buildShock(), mitigations, currency });
      setMitResult(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Mitigation run failed.");
    } finally {
      setMitLoading(false);
    }
  };

  const runReverse = async () => {
    if (!effectiveTarget) return;
    setRevLoading(true);
    try {
      const { data } = await api.post("/scenario/reverse", {
        shock: buildShock(), target_metric: revMetric, threshold: Number(revThreshold), currency,
      });
      setRevResult(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reverse stress failed.");
    } finally {
      setRevLoading(false);
    }
  };

  const save = async () => {
    if (!saveName.trim()) return toast.error("Name your scenario first.");
    setSaving(true);
    try {
      await api.post("/scenario/library", { name: saveName.trim(), shock: buildShock(), currency });
      toast.success("Scenario saved to your library.");
      setSaveName("");
      loadLibrary();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/scenario/library/${id}`);
      setSelected((s) => s.filter((x) => x !== id));
      loadLibrary();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete.");
    }
  };

  const loadScenario = (sc) => {
    const sh = sc.shock;
    setType(sh.type);
    setSeverity(sh.severity ?? 0.7);
    setCurrency(sc.currency || "GBP");
    if (sh.type === "geo") { setGeoKind(sh.geo.shock_type); setTarget(sh.geo.target); }
    else setTarget(sh.target_ids?.[0] || "");
    setResult(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleSelect = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length < 4 ? [...s, id] : s));

  const runCompare = async () => {
    if (selected.length < 2) return toast.error("Select 2–4 scenarios to compare.");
    setComparing(true);
    try {
      const { data } = await api.post("/scenario/compare", { scenario_ids: selected });
      setCompare(data.scenarios);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Compare failed.");
    } finally {
      setComparing(false);
    }
  };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Seo title="Scenario Studio" description="Probabilistic scenario planning — Monte-Carlo loss-exceedance, saved library and comparison." path="/scenario-studio" />
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-12" data-testid="scenario-studio">
        <p className="ek-eyebrow flex items-center gap-1"><FlaskConical size={12} /> Scenario Studio</p>
        <h1 className="ek-heading mt-2 text-4xl sm:text-5xl">Probabilistic scenario planning</h1>
        <p className="mt-3 max-w-2xl text-base text-ek-ink/65">
          Shock the enterprise and see the full <strong>loss-exceedance curve</strong> (Monte-Carlo over your FAIR
          model), not just a single number — then save, reuse and compare scenarios.
        </p>

        <div className="mt-8 grid gap-6 lg:grid-cols-5">
          {/* Builder */}
          <div className="space-y-4 lg:col-span-2">
            <div className="rounded-md border border-black/5 bg-white p-5">
              <div className="flex gap-1 rounded-md border border-black/10 p-0.5">
                {TYPES.map((t) => (
                  <button key={t.key} onClick={() => { setType(t.key); setResult(null); }}
                    data-testid={`studio-type-${t.key}`}
                    className={`flex-1 rounded px-2 py-1.5 text-xs font-semibold transition-colors ${type === t.key ? "bg-ek-ink text-white" : "text-ek-ink/65 hover:text-ek-ink"}`}>
                    {t.label}
                  </button>
                ))}
              </div>

              {type === "geo" && (
                <div className="mt-3 grid grid-cols-2 gap-1 rounded-md border border-black/10 p-0.5">
                  {GEO_KINDS.map((g) => (
                    <button key={g.key} onClick={() => { setGeoKind(g.key); setResult(null); }}
                      data-testid={`studio-geokind-${g.key}`}
                      className={`rounded px-2 py-1.5 text-xs font-semibold transition-colors ${geoKind === g.key ? "bg-ek-purple text-white" : "text-ek-ink/65 hover:text-ek-ink"}`}>
                      {g.label}
                    </button>
                  ))}
                </div>
              )}

              <label className="mt-4 block text-xs font-medium text-ek-ink/65">Target</label>
              <select value={effectiveTarget} onChange={(e) => { setTarget(e.target.value); setResult(null); }}
                data-testid="studio-target-select"
                className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-ek-purple">
                {targetList.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>

              <div className="mt-4 mb-1 flex items-center justify-between">
                <label className="text-xs font-medium text-ek-ink/65">Shock severity</label>
                <span className="text-xs font-bold text-ek-purple" data-testid="studio-severity-value">{Math.round(severity * 100)}%</span>
              </div>
              <input type="range" min="0.1" max="1" step="0.05" value={severity}
                onChange={(e) => setSeverity(parseFloat(e.target.value))}
                data-testid="studio-severity-slider" className="w-full accent-ek-purple" />

              <div className="mt-4 flex items-center justify-between gap-2">
                <div className="flex gap-1 rounded-md border border-black/10 p-0.5">
                  {CURRENCIES.map((c) => (
                    <button key={c} onClick={() => setCurrency(c)}
                      className={`rounded px-2.5 py-1 text-xs font-semibold ${currency === c ? "bg-ek-ink text-white" : "text-ek-ink/65"}`}>{c}</button>
                  ))}
                </div>
                <button onClick={run} disabled={loading || !effectiveTarget}
                  data-testid="studio-run-button"
                  className="ek-btn flex items-center gap-1.5 !px-5 disabled:opacity-50">
                  {loading ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} Run
                </button>
              </div>
            </div>

            {/* Save */}
            <div className="flex items-center gap-2 rounded-md border border-black/5 bg-white p-3">
              <input value={saveName} onChange={(e) => setSaveName(e.target.value)}
                placeholder="Name this scenario…" data-testid="studio-save-name"
                className="flex-1 rounded-sm border border-black/15 px-3 py-1.5 text-sm focus:border-ek-purple focus:outline-none" />
              <button onClick={save} disabled={saving} data-testid="studio-save-button"
                className="flex items-center gap-1.5 rounded-sm border border-ek-purple px-3 py-1.5 text-sm font-semibold text-ek-purple hover:bg-ek-purple/5 disabled:opacity-50">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
              </button>
            </div>
          </div>

          {/* Results */}
          <div className="lg:col-span-3">
            <div className="mb-4 flex gap-1 rounded-md border border-black/10 bg-white p-0.5">
              {[
                { key: "forecast", label: "Forecast", icon: Play },
                { key: "mitigate", label: "Mitigation what-if", icon: ShieldCheck },
                { key: "reverse", label: "Reverse stress test", icon: Target },
              ].map((m) => (
                <button key={m.key} onClick={() => setMode(m.key)}
                  data-testid={`studio-mode-${m.key}`}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded px-2 py-1.5 text-xs font-semibold transition-colors ${mode === m.key ? "bg-ek-purple text-white" : "text-ek-ink/65 hover:text-ek-ink"}`}>
                  <m.icon size={13} /> {m.label}
                </button>
              ))}
            </div>

            {mode === "forecast" && (!result ? (
              <div className="flex h-full min-h-[300px] items-center justify-center rounded-md border border-dashed border-black/10 bg-white/50 text-sm text-ek-ink/65" data-testid="studio-empty">
                Configure a shock and run to see the loss-exceedance curve.
              </div>
            ) : (
              <div className="space-y-4" data-testid="studio-result">
                <div className="rounded-md border border-black/5 bg-white p-5">
                  <div className="flex flex-wrap gap-5 text-sm">
                    <Metric icon={Banknote} label="Mean ALE">
                      <span>{fmt(result.fair.baseline_ale, result.currency)}</span>
                      <ArrowRight size={12} className="text-ek-ink/30" />
                      <span className="font-bold text-red-600">{fmt(result.fair.scenario_ale, result.currency)}</span>
                    </Metric>
                    <Metric icon={AlertTriangle} label="P95 loss">
                      <span className="font-bold text-red-600" data-testid="studio-p95">{fmt(result.fair.scenario.p95, result.currency)}</span>
                    </Metric>
                    <Metric icon={Gauge} label="Posture">
                      <span>{result.posture.baseline}</span>
                      <ArrowRight size={12} className="text-ek-ink/30" />
                      <span className={`font-bold ${posTone(result.posture.delta)}`}>{result.posture.scenario}</span>
                    </Metric>
                    <Metric icon={Layers} label="Breached">
                      <span className="font-bold text-red-600">{result.breached_services}</span>
                    </Metric>
                  </div>
                </div>
                <div className="rounded-md border border-black/5 bg-white p-5">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-ek-ink/65">
                    Loss-exceedance curve · annual loss at each probability
                  </p>
                  <ExceedanceCurve series={[
                    { name: "Baseline", color: "#94a3b8", points: result.fair.baseline_exceedance },
                    { name: "Scenario", color: "#ef4444", points: result.fair.scenario_exceedance },
                  ]} />
                  <p className="mt-1 text-xs text-ek-ink/65">
                    Read it as: "there's an X% chance annual loss exceeds £Y". P90 scenario loss:
                    <strong className="text-ek-ink"> {fmt(result.fair.scenario.p90, result.currency)}</strong>.
                  </p>
                </div>
              </div>
            ))}

            {mode === "mitigate" && (
              <div className="space-y-4" data-testid="studio-mitigate">
                <div className="rounded-md border border-black/5 bg-white p-5">
                  <p className="mb-3 text-xs font-medium uppercase tracking-wide text-ek-ink/65">Apply mitigations to the configured shock</p>
                  <Slider label="Control effectiveness" value={mitControl} onChange={setMitControl} testid="mit-control" />
                  <Slider label="Inventory / severity buffer" value={mitInventory} onChange={setMitInventory} testid="mit-inventory" />
                  <label className="mt-3 flex items-center gap-2 text-sm text-ek-ink/70">
                    <input type="checkbox" checked={mitAlt} onChange={(e) => setMitAlt(e.target.checked)}
                      data-testid="mit-alt" className="accent-ek-purple" />
                    Activate alternate supplier (removes single-point-of-failure, −40% severity)
                  </label>
                  <button onClick={runMitigate} disabled={mitLoading || !effectiveTarget}
                    data-testid="studio-mitigate-run"
                    className="ek-btn mt-4 flex items-center gap-1.5 !px-5 disabled:opacity-50">
                    {mitLoading ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} Run mitigation what-if
                  </button>
                </div>
                {mitResult && (
                  <>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                      <Avoided label="Avoided mean loss" value={fmt(mitResult.avoided.mean, mitResult.currency)} testid="mit-avoided-mean" />
                      <Avoided label="Avoided P95 loss" value={fmt(mitResult.avoided.p95, mitResult.currency)} testid="mit-avoided-p95" />
                      <Avoided label="Posture improvement" value={`−${mitResult.avoided.posture}`} testid="mit-avoided-posture" />
                    </div>
                    <div className="rounded-md border border-black/5 bg-white p-5">
                      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-ek-ink/65">Gross vs residual loss-exceedance</p>
                      <ExceedanceCurve series={[
                        { name: "Gross (no action)", color: "#ef4444", points: mitResult.gross.fair.scenario_exceedance },
                        { name: "Residual (mitigated)", color: "#10b981", points: mitResult.residual.fair.scenario_exceedance },
                      ]} />
                    </div>
                  </>
                )}
              </div>
            )}

            {mode === "reverse" && (
              <div className="space-y-4" data-testid="studio-reverse">
                <div className="rounded-md border border-black/5 bg-white p-5">
                  <p className="mb-3 text-xs font-medium uppercase tracking-wide text-ek-ink/65">
                    Find the breaking point — the minimum shock that breaches your target
                  </p>
                  <div className="flex flex-wrap items-end gap-3">
                    <div>
                      <label className="mb-1 block text-xs text-ek-ink/65">Target metric</label>
                      <select value={revMetric} onChange={(e) => setRevMetric(e.target.value)}
                        data-testid="rev-metric"
                        className="rounded-md border border-black/10 px-3 py-2 text-sm focus:border-ek-purple focus:outline-none">
                        <option value="posture">Posture score</option>
                        <option value="financial">Financial loss (£ ALE)</option>
                        <option value="services">Services breached</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-ek-ink/65">Breaches when ≥</label>
                      <input type="number" value={revThreshold} onChange={(e) => setRevThreshold(e.target.value)}
                        data-testid="rev-threshold"
                        className="w-40 rounded-md border border-black/10 px-3 py-2 text-sm focus:border-ek-purple focus:outline-none" />
                    </div>
                    <button onClick={runReverse} disabled={revLoading || !effectiveTarget}
                      data-testid="studio-reverse-run"
                      className="ek-btn flex items-center gap-1.5 !px-5 disabled:opacity-50">
                      {revLoading ? <Loader2 size={15} className="animate-spin" /> : <Target size={15} />} Find breaking point
                    </button>
                  </div>
                </div>
                {revResult && (
                  <div className="rounded-md border border-black/5 bg-white p-5">
                    <p className="text-sm" data-testid="rev-verdict">
                      {revResult.breaches ? (
                        <span className="flex items-center gap-2">
                          <TrendingDown size={16} className="text-red-600" />
                          Breaches at <strong className="text-red-600">{Math.round(revResult.breaking_point.severity * 100)}% severity</strong>
                          {" "}(metric reaches {revResult.breaking_point.value}).
                        </span>
                      ) : (
                        <span className="text-emerald-700">Resilient — the target is not breached even at 100% severity.</span>
                      )}
                    </p>
                    <div className="mt-3" style={{ width: "100%", height: 240 }}>
                      <ResponsiveContainer>
                        <LineChart data={revResult.scan} margin={{ top: 8, right: 16, left: 4, bottom: 16 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#00000010" />
                          <XAxis dataKey="severity" tick={{ fontSize: 11, fill: "#6b6b78" }}
                            tickFormatter={(v) => `${Math.round(v * 100)}%`}
                            label={{ value: "Shock severity", position: "insideBottom", offset: -8, fontSize: 11, fill: "#9a9aa6" }} />
                          <YAxis dataKey={revMetric === "financial" ? "scenario_ale" : revMetric === "posture" ? "posture" : "breached_services"}
                            tick={{ fontSize: 11, fill: "#6b6b78" }} width={64} />
                          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                          <ReferenceLine y={Number(revThreshold)} stroke="#ef4444" strokeDasharray="4 4"
                            label={{ value: "threshold", fontSize: 10, fill: "#ef4444" }} />
                          <Line type="monotone"
                            dataKey={revMetric === "financial" ? "scenario_ale" : revMetric === "posture" ? "posture" : "breached_services"}
                            stroke="#7737F6" strokeWidth={2.5} dot={{ r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Library */}
        <section className="mt-12" data-testid="studio-library">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="ek-eyebrow flex items-center gap-1"><Layers size={14} /> Scenario library</h2>
            <button onClick={runCompare} disabled={comparing || selected.length < 2}
              data-testid="studio-compare-button"
              className="flex items-center gap-1.5 rounded-sm bg-ek-ink px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40">
              {comparing ? <Loader2 size={14} className="animate-spin" /> : <GitCompareArrows size={14} />}
              Compare ({selected.length})
            </button>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="studio-library-list">
            {library.map((sc) => (
              <div key={sc.scenario_id} data-testid={`studio-scenario-${sc.scenario_id}`}
                className={`rounded-md border bg-white p-4 transition-colors ${selected.includes(sc.scenario_id) ? "border-ek-purple ring-1 ring-ek-purple/30" : "border-black/5"}`}>
                <div className="flex items-start justify-between gap-2">
                  <label className="flex items-start gap-2">
                    <input type="checkbox" checked={selected.includes(sc.scenario_id)}
                      onChange={() => toggleSelect(sc.scenario_id)}
                      data-testid={`studio-select-${sc.scenario_id}`}
                      className="mt-1 accent-ek-purple" />
                    <span>
                      <span className="block text-sm font-semibold text-ek-ink">{sc.name}</span>
                      {sc.is_template && <span className="rounded-full bg-ek-purple/10 px-1.5 py-0.5 text-[10px] font-semibold text-ek-purple">Template</span>}
                    </span>
                  </label>
                  {!sc.is_template && (
                    <button onClick={() => del(sc.scenario_id)} data-testid={`studio-delete-${sc.scenario_id}`}
                      className="text-ek-ink/30 transition-colors hover:text-red-600"><Trash2 size={14} /></button>
                  )}
                </div>
                {sc.description && <p className="mt-2 text-xs text-ek-ink/65">{sc.description}</p>}
                {sc.last_result_summary && (
                  <p className="mt-2 text-xs text-ek-ink/65">
                    Posture <strong className="text-ek-ink">{sc.last_result_summary.posture?.scenario}</strong> ·
                    P95 <strong className="text-red-600"> {fmt(sc.last_result_summary.fair?.scenario_p95, sc.currency)}</strong>
                  </p>
                )}
                <button onClick={() => loadScenario(sc)} data-testid={`studio-load-${sc.scenario_id}`}
                  className="mt-3 text-xs font-semibold text-ek-purple hover:underline">Load into builder →</button>
              </div>
            ))}
          </div>
        </section>

        {/* Comparison */}
        {compare && (
          <section className="mt-10 rounded-md border border-ek-purple/20 bg-white p-6" data-testid="studio-compare-result">
            <h2 className="flex items-center gap-2 font-display text-xl font-bold text-ek-ink">
              <GitCompareArrows size={18} className="text-ek-purple" /> Scenario comparison
            </h2>
            <div className="mt-4">
              <ExceedanceCurve height={300} series={compare.map((s, i) => ({
                name: s.name, color: COMPARE_COLORS[i % COMPARE_COLORS.length],
                points: s.scenario_exceedance,
              }))} />
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm" data-testid="studio-compare-table">
                <thead>
                  <tr className="border-b border-black/10 text-left text-xs uppercase text-ek-ink/65">
                    <th className="py-2">Scenario</th><th>Posture</th><th>Mean ALE</th>
                    <th>P90</th><th>P95</th><th>Breached</th><th>Headline impact</th>
                  </tr>
                </thead>
                <tbody>
                  {compare.map((s) => (
                    <tr key={s.scenario_id} className="border-b border-black/5">
                      <td className="py-2 font-semibold text-ek-ink">{s.name}</td>
                      <td className={`font-bold ${posTone(s.result.posture.delta)}`}>{s.result.posture.scenario}</td>
                      <td>{fmt(s.result.fair.scenario_ale, s.result.currency)}</td>
                      <td>{fmt(s.result.fair.scenario_p90 ?? s.result.fair.scenario?.p90, s.result.currency)}</td>
                      <td className="font-semibold text-red-600">{fmt(s.result.fair.scenario_p95, s.result.currency)}</td>
                      <td>{s.result.breached_services}</td>
                      <td className="font-semibold">{fmt(s.result.headline_financial_impact, s.result.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

const Metric = ({ icon: Icon, label, children }) => (
  <div className="flex items-center gap-2">
    <Icon size={14} className="text-ek-ink/35" />
    <span className="text-xs text-ek-ink/65">{label}</span>
    <span className="flex items-center gap-1">{children}</span>
  </div>
);

const Slider = ({ label, value, onChange, testid }) => (
  <div className="mt-3">
    <div className="mb-1 flex items-center justify-between">
      <label className="text-xs font-medium text-ek-ink/65">{label}</label>
      <span className="text-xs font-bold text-ek-purple">{Math.round(value * 100)}%</span>
    </div>
    <input type="range" min="0" max="0.9" step="0.05" value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      data-testid={testid} className="w-full accent-ek-purple" />
  </div>
);

const Avoided = ({ label, value, testid }) => (
  <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4" data-testid={testid}>
    <p className="text-xs font-medium text-emerald-700/70">{label}</p>
    <p className="mt-0.5 font-display text-2xl font-bold text-emerald-700">{value}</p>
  </div>
);
